<template>
  <div class="course-wizard">
    <Sidebar />
    <div class="wizard-content">
      <!-- Progress Bar -->
      <div class="wizard-progress">
        <div class="progress-steps">
          <div 
            v-for="(step, index) in steps" 
            :key="step.id"
            class="step"
            :class="{ 
              'active': currentStep === index,
              'completed': currentStep > index 
            }"
          >
            <div class="step-number">{{ index + 1 }}</div>
            <div class="step-label">{{ step.label }}</div>
          </div>
        </div>
      </div>

      <!-- Success/Error Messages -->
      <div v-if="successMessage" class="success-message">
        {{ successMessage }}
      </div>
      <div v-if="errorMessage" class="error-message">
        {{ errorMessage }}
      </div>

      <!-- Step Content -->
      <div class="step-content">
        <!-- Step 1: Basic Course Information -->
        <div v-if="currentStep === 0" class="step-container">
          <h2>Basic Course Information</h2>
          <form @submit.prevent="handleBasicInfoSubmit" class="form-container">
            <div class="form-section">
              <div class="form-group">
                <label for="title">Course Title *</label>
                <input
                  id="title"
                  v-model="courseData.title"
                  type="text"
                  class="form-control"
                />
              </div>
              <div class="form-group">
                <label for="title_ar">Course Title (Arabic) *</label>
                <input
                  id="title_ar"
                  v-model="courseData.title_ar"
                  type="text"
                  class="form-control"
                  dir="rtl"
                  lang="ar"
                />
              </div>
              <div class="form-group">
                <label for="description">Course Description *</label>
                <QuillRichTextEditor
                  id="description"
                  v-model="courseData.description"
                  label=""
                  placeholder="Enter course description"
                  :minHeight="'250px'"
                />
              </div>
              <div class="form-group">
                <label for="description_ar">Course Description (Arabic) *</label>
                <QuillRichTextEditor
                  id="description_ar"
                  v-model="courseData.description_ar"
                  label=""
                  placeholder="أدخل وصف الدورة"
                  :minHeight="'250px'"
                  :toolbar="arabicToolbar"
                />
              </div>
            </div>

            <div class="form-section">
              <div class="form-group">
                <label for="price">Price *</label>
                <input
                  id="price"
                  v-model.number="courseData.price"
                  type="number"
                  step="0.01"
                  class="form-control"
                />
              </div>
              <div class="form-group">
                <label for="minutes">Duration (minutes) *</label>
                <input
                  id="minutes"
                  v-model.number="courseData.minutes"
                  type="number"
                  min="1"
                  class="form-control"
                />
              </div>
            </div>
          </form>
        </div>

        <!-- Step 2: Media and URLs -->
        <div v-if="currentStep === 1" class="step-container">
          <h2>Media and URLs</h2>
          <div class="form-section">
            <div class="form-group">
              <label for="audio_cover">Audio Cover Image *</label>
              <div class="file-uploader">
                <button @click="openUploadWidget('audio_cover')" class="upload-btn">
                  <i class="fas fa-upload"></i> Upload Cover Image
                </button>
                <div v-if="courseData.audio_cover" class="uploaded-file">
                  <img :src="courseData.audio_cover" alt="Cover" class="preview-image">
                </div>
              </div>
            </div>
            <div class="form-group">
              <label for="audio_url">Audio File *</label>
              <div class="file-uploader">
                <button @click="openUploadWidget('audio_url')" class="upload-btn">
                  <i class="fas fa-upload"></i> Upload Audio File
                </button>
                <div v-if="courseData.audio_url" class="uploaded-file">
                  <p>Audio uploaded successfully</p>
                </div>
              </div>
            </div>
            <div class="form-group">
              <label for="preview_url">Preview Video</label>
              <div class="file-uploader">
                <button @click="openUploadWidget('preview_url')" class="upload-btn">
                  <i class="fas fa-upload"></i> Upload Preview Video
                </button>
                <div v-if="courseData.preview_url" class="uploaded-file">
                  <p>Preview video uploaded successfully</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Step 3: Course Lessons -->
        <div v-if="currentStep === 2" class="step-container">
          <h2>Course Lessons</h2>
          <div class="lessons-manager">
            <button @click="openAddLessonModal" class="add-lesson-btn">
              Add New Lesson
            </button>

            <div class="lessons-list">
              <div v-for="(lesson, index) in courseLessons" :key="index" class="lesson-item">
                <div class="lesson-info">
                  <span class="lesson-number">#{{ index + 1 }}</span>
                  <span class="lesson-title">{{ lesson.title }}</span>
                  <span v-if="lesson.lesson_url" class="video-status small" :class="lesson.lesson_status">
                    {{ lesson.lesson_status }}
                  </span>
                </div>
                <div class="lesson-actions">
                  <button v-if="lesson.lesson_url" @click="playVideo(lesson.lesson_url)" class="play-btn">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="play-icon">
                      <polygon points="5 3 19 12 5 21 5 3"/>
                    </svg>
                  </button>
                  <button @click="editLesson(index)" class="edit-btn">Edit</button>
                  <button @click="deleteLesson(index)" class="delete-btn">Delete</button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Step 4: Review and Publish -->
        <div v-if="currentStep === 3" class="step-container">
          <h2>Review and Publish</h2>
          <div class="review-section">
            <h3>Course Details</h3>
            <div class="review-item">
              <strong>Title:</strong> {{ courseData.title }}
            </div>
            <div class="review-item">
              <strong>Arabic Title:</strong> {{ courseData.title_ar }}
            </div>
            <div class="review-item">
              <strong>Price:</strong> ${{ courseData.price }}
            </div>
            <div class="review-item">
              <strong>Duration:</strong> {{ courseData.minutes }} minutes
            </div>
            
            <h3>Media Files</h3>
            <div class="review-item">
              <strong>Cover Image:</strong> 
              <img v-if="courseData.audio_cover" :src="courseData.audio_cover" class="preview-image" />
            </div>
            
            <h3>Lessons ({{ courseLessons.length }})</h3>
            <div class="review-lessons">
              <div v-for="(lesson, index) in courseLessons" :key="index" class="review-lesson">
                <div class="review-lesson-header">
                  <span class="lesson-number">{{ index + 1 }}.</span>
                  <span class="lesson-title">{{ lesson.title }}</span>
                  <span class="lesson-duration">{{ lesson.duration }} min</span>
                </div>
                <div v-if="lesson.thumbnail" class="lesson-thumbnail">
                  <img :src="lesson.thumbnail" alt="Lesson thumbnail" />
                </div>
                <div v-if="lesson.lesson_url" class="lesson-video-info">
                  <span class="video-status" :class="lesson.lesson_status">{{ lesson.lesson_status }}</span>
                  <button @click="playVideo(lesson.lesson_url)" class="preview-btn small">
                    Preview Video
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Navigation Buttons -->
        <div class="wizard-navigation">
          <button 
            v-if="currentStep > 0" 
            @click="previousStep"
            class="nav-button previous"
          >
            Previous
          </button>
          <button 
            v-if="currentStep < steps.length - 1" 
            @click="nextStep"
            class="nav-button next"
            :disabled="!canProceed"
          >
            Next
          </button>
          <button 
            v-if="currentStep === steps.length - 1" 
            @click="publishCourse"
            class="nav-button publish"
            :disabled="isPublishing"
          >
            {{ isPublishing ? 'Publishing...' : 'Publish Course' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Add/Edit Lesson Modal -->
    <div v-if="showLessonModal" class="modal">
      <div class="modal-content">
        <div class="modal-header">
          <h3>{{ editingLessonIndex === null ? 'Add New Lesson' : 'Edit Lesson' }}</h3>
          <button class="close-btn" @click="closeLessonModal">&times;</button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="saveLessonForm">
            <div class="form-group">
              <label for="lessonTitle">Lesson Title *</label>
              <input
                id="lessonTitle"
                v-model="lessonForm.title"
                type="text"
              />
            </div>
            <div class="form-group">
              <label for="lessonDescription">Description *</label>
              <textarea
                id="lessonDescription"
                v-model="lessonForm.description"
                rows="4"
              ></textarea>
            </div>
            <div class="form-group">
              <label for="lessonDuration">Duration (minutes) *</label>
              <input
                id="lessonDuration"
                v-model.number="lessonForm.duration"
                type="number"
                min="1"
              />
            </div>
            <div class="form-group">
              <label for="existingVideo">Select Existing Video</label>
              <div v-if="loadingLessons" class="loading-indicator">Loading videos...</div>
              <select 
                v-else
                id="existingVideo" 
                class="form-control"
                @change="selectExistingVideo($event.target.value)"
              >
                <option value="">-- Select a video --</option>
                <option 
                  v-for="lesson in existingLessons" 
                  :key="lesson.id" 
                  :value="lesson.id"
                  :selected="lessonForm.lesson_url === lesson.lesson_url"
                >
                  {{ lesson.title }} ({{ lesson.lesson_status }})
                </option>
              </select>
            </div>
            <div class="form-group">
              <label>Or Upload New Video</label>
              <div class="file-input-container">
                <input
                  id="lessonVideo"
                  type="file"
                  accept="video/*"
                  @change="handleVideoUpload"
                  class="file-input"
                />
                <label for="lessonVideo" class="file-input-label">Choose File</label>
                <span class="file-name">{{ lessonForm.video ? lessonForm.video.fileUrl?.split('/').pop() : 'No file chosen' }}</span>
              </div>
              <div v-if="lessonForm.lesson_url" class="video-preview">
                <div class="video-info">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="video-icon">
                    <polygon points="23 7 16 12 23 17 23 7"></polygon>
                    <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
                  </svg>
                  <span class="video-name">{{ lessonForm.lesson_url.split('/').pop() }}</span>
                </div>
                <button type="button" @click="playVideo(lessonForm.lesson_url)" class="preview-btn">
                  Preview Video
                </button>
              </div>
            </div>
            <div class="form-group">
              <label for="lessonThumbnail">Lesson Thumbnail</label>
              <div class="file-input-container">
                <input
                  id="lessonThumbnail"
                  type="file"
                  accept="image/*"
                  @change="handleThumbnailUpload"
                  class="file-input"
                />
                <label for="lessonThumbnail" class="file-input-label">Choose File</label>
                <span class="file-name">{{ thumbnailPreview ? 'Image selected' : 'No file chosen' }}</span>
              </div>
              <div v-if="thumbnailPreview" class="thumbnail-preview">
                <img :src="thumbnailPreview" alt="Thumbnail preview" />
              </div>
            </div>
            <div class="form-actions">
              <button type="button" @click="closeLessonModal" class="cancel-btn">Cancel</button>
              <button type="submit" class="save-btn">Save Lesson</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import Sidebar from './Sidebar.vue'
import QuillRichTextEditor from './QuillEditor.vue'
import cloudflareR2Service from '@/services/cloudflareR2'

export default {
  name: 'CourseCreationWizard',
  components: {
    Sidebar,
    QuillRichTextEditor
  },
  setup() {
    const router = useRouter()
    const currentStep = ref(0)
    const isPublishing = ref(false)
    const successMessage = ref('')
    const errorMessage = ref('')
    const showLessonModal = ref(false)
    const editingLessonIndex = ref(null)
    
    const steps = [
      { id: 'basic-info', label: 'Basic Information' },
      { id: 'media', label: 'Media & URLs' },
      { id: 'lessons', label: 'Lessons' },
      { id: 'review', label: 'Review & Publish' }
    ]

    const courseData = ref({
      title: '',
      title_ar: '',
      description: '',
      description_ar: '',
      price: 99.99,
      minutes: 60,
      audio_cover: '',
      audio_cover_ar: '',
      audio_url: '',
      audio_url_ar: '',
      preview_url: '',
      is_active: true,
      is_premium: false
    })

    const courseLessons = ref([])
    const existingLessons = ref([])
    const loadingLessons = ref(false)
    
    const lessonForm = ref({
      title: '',
      description: '',
      duration: 30,
      video: null,
      thumbnail: null,
      lesson_url: '',
      lesson_status: 'pending'
    })

    const thumbnailPreview = ref(null)

    const canProceed = computed(() => {
      // Always allow proceeding to the next step regardless of field completion
      return true
      
      // Original validation logic (commented out)
      /*
      switch (currentStep.value) {
        case 0:
          return courseData.value.title && 
                 courseData.value.title_ar && 
                 courseData.value.description && 
                 courseData.value.description_ar
        case 1:
          return courseData.value.audio_cover && 
                 courseData.value.audio_url
        case 2:
          return courseLessons.value.length > 0
        default:
          return true
      }
      */
    })

    const arabicToolbar = [
      ['bold', 'italic', 'underline'],
      [{ 'direction': 'rtl' }],
      [{ 'align': ['', 'right', 'center'] }]
    ]

    const nextStep = () => {
      if (currentStep.value < steps.length - 1) {
        currentStep.value++
      }
    }

    const previousStep = () => {
      if (currentStep.value > 0) {
        currentStep.value--
      }
    }

    const openUploadWidget = async (field) => {
      // Create a file input element programmatically
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = field.includes('url') ? 'audio/*,video/*' : 'image/*'
      
      input.onchange = async (event) => {
        const file = event.target.files[0]
        if (!file) return
        
        try {
          const uploadResponse = await cloudflareR2Service.uploadWithValidation(file)
          courseData.value[field] = uploadResponse.fileUrl
        } catch (error) {
          errorMessage.value = `Upload failed: ${error.message || "Unknown error"}`
        }
      }
      
      input.click()
    }

    const handleVideoUpload = async (event) => {
      const file = event.target.files[0]
      if (file) {
        try {
          const uploadResponse = await cloudflareR2Service.uploadWithValidation(file)
          lessonForm.value.lesson_url = uploadResponse.fileUrl
          lessonForm.value.video = uploadResponse
        } catch (error) {
          errorMessage.value = `Video upload failed: ${error.message || "Unknown error"}`
        }
      }
    }

    const handleThumbnailUpload = async (event) => {
      const file = event.target.files[0]
      if (file) {
        try {
          const uploadResponse = await cloudflareR2Service.uploadWithValidation(file)
          lessonForm.value.thumbnail = uploadResponse
          thumbnailPreview.value = uploadResponse.fileUrl
        } catch (error) {
          errorMessage.value = `Thumbnail upload failed: ${error.message || "Unknown error"}`
        }
      }
    }

    const playVideo = (url) => {
      window.open(url, '_blank')
    }

    const selectExistingVideo = (lessonId) => {
      if (!lessonId) {
        // Clear selection
        lessonForm.value.lesson_url = ''
        lessonForm.value.video = null
        return
      }
      
      const selectedLesson = existingLessons.value.find(lesson => lesson.id.toString() === lessonId.toString())
      if (selectedLesson) {
        lessonForm.value.lesson_url = selectedLesson.lesson_url || ''
        lessonForm.value.video = { fileUrl: selectedLesson.lesson_url }
        lessonForm.value.title = lessonForm.value.title || selectedLesson.title
        lessonForm.value.description = lessonForm.value.description || selectedLesson.description
        lessonForm.value.duration = lessonForm.value.duration || selectedLesson.duration
        
        // If the selected lesson has a thumbnail, use it
        if (selectedLesson.thumbnail) {
          thumbnailPreview.value = selectedLesson.thumbnail
        }
      }
    }

    const fetchExistingLessons = async () => {
      try {
        loadingLessons.value = true
        const token = localStorage.getItem('token')
        
        const response = await fetch('https://api.kewaar.com/api/admin/all-lessons', {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          }
        })
        
        const result = await response.json()
        
        if (response.ok) {
          existingLessons.value = result.data.lessons || []
        } else {
          errorMessage.value = result.message || 'Failed to fetch existing lessons'
        }
      } catch (error) {
        errorMessage.value = error.message || 'Failed to fetch existing lessons'
      } finally {
        loadingLessons.value = false
      }
    }

    const openAddLessonModal = () => {
      // Fetch the latest videos if needed
      if (existingLessons.value.length === 0) {
        fetchExistingLessons()
      }
      
      editingLessonIndex.value = null
      lessonForm.value = {
        title: '',
        description: '',
        duration: 30,
        video: null,
        thumbnail: null,
        lesson_url: '',
        lesson_status: 'pending'
      }
      thumbnailPreview.value = null
      showLessonModal.value = true
    }

    const editLesson = (index) => {
      // Fetch the latest videos if needed
      if (existingLessons.value.length === 0) {
        fetchExistingLessons()
      }
      
      editingLessonIndex.value = index
      const lesson = courseLessons.value[index]
      lessonForm.value = { ...lesson }
      
      // If the lesson has a video URL, set the video object
      if (lesson.lesson_url) {
        lessonForm.value.video = { fileUrl: lesson.lesson_url }
      }
      
      thumbnailPreview.value = lesson.thumbnail || null
      showLessonModal.value = true
    }

    const deleteLesson = (index) => {
      courseLessons.value.splice(index, 1)
    }

    const closeLessonModal = () => {
      showLessonModal.value = false
      editingLessonIndex.value = null
      thumbnailPreview.value = null
    }

    const saveLessonForm = () => {
      const lessonData = { 
        title: lessonForm.value.title,
        description: lessonForm.value.description,
        duration: lessonForm.value.duration,
        lesson_url: lessonForm.value.lesson_url || '',
        thumbnail: thumbnailPreview.value || '',
        lesson_status: lessonForm.value.video ? 'uploaded' : 'pending'
      }
      
      if (editingLessonIndex.value !== null) {
        courseLessons.value[editingLessonIndex.value] = lessonData
      } else {
        courseLessons.value.push(lessonData)
      }
      
      closeLessonModal()
    }

    const publishCourse = async () => {
      try {
        isPublishing.value = true
        const token = localStorage.getItem('token')
        
        // First create the course
        const courseResponse = await fetch('https://api.kewaar.com/api/admin/courses', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(courseData.value)
        })

        const courseResult = await courseResponse.json()
        
        if (!courseResponse.ok) {
          throw new Error(courseResult.message || 'Failed to create course')
        }

        const courseId = courseResult.data.id

        // Then create all lessons
        for (const lesson of courseLessons.value) {
          await fetch('https://api.kewaar.com/api/admin/lessons1', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              ...lesson,
              course_id: courseId
            })
          })
        }

        successMessage.value = 'Course published successfully!'
        setTimeout(() => {
          router.push('/courses')
        }, 2000)
      } catch (error) {
        errorMessage.value = error.message || 'Failed to publish course'
      } finally {
        isPublishing.value = false
      }
    }

    // Fetch existing lessons when component is mounted
    onMounted(() => {
      fetchExistingLessons()
    })

    return {
      currentStep,
      steps,
      courseData,
      courseLessons,
      lessonForm,
      showLessonModal,
      editingLessonIndex,
      successMessage,
      errorMessage,
      isPublishing,
      canProceed,
      arabicToolbar,
      thumbnailPreview,
      existingLessons,
      loadingLessons,
      nextStep,
      previousStep,
      openUploadWidget,
      handleVideoUpload,
      handleThumbnailUpload,
      playVideo,
      selectExistingVideo,
      fetchExistingLessons,
      openAddLessonModal,
      editLesson,
      deleteLesson,
      closeLessonModal,
      saveLessonForm,
      publishCourse
    }
  }
}
</script>

<style scoped>
.course-wizard {
  display: flex;
  min-height: 100vh;
  background: #f8fafc;
}

.wizard-content {
  flex: 1;
  margin-left: 280px;
  padding: 2rem;
}

.wizard-progress {
  margin-bottom: 2rem;
}

.progress-steps {
  display: flex;
  justify-content: space-between;
  margin: 0 auto;
  max-width: 800px;
}

.step {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  flex: 1;
}

.step::before {
  content: '';
  position: absolute;
  top: 20px;
  left: calc(-50% + 20px);
  right: calc(50% + 20px);
  height: 2px;
  background: #e2e8f0;
  z-index: 0;
}

.step:first-child::before {
  display: none;
}

.step.active .step-number {
  background: #2563eb;
  color: white;
}

.step.completed .step-number {
  background: #10b981;
  color: white;
}

.step-number {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #e2e8f0;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  margin-bottom: 0.5rem;
  position: relative;
  z-index: 1;
}

.step-label {
  font-size: 0.875rem;
  color: #64748b;
  font-weight: 500;
}

.step.active .step-label {
  color: #2563eb;
  font-weight: 600;
}

.step-container {
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.form-section {
  margin-bottom: 2rem;
  padding-bottom: 2rem;
  border-bottom: 1px solid #e2e8f0;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 0.5rem;
}

.form-control {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.9375rem;
  transition: all 0.2s ease;
  background: #f8fafc;
}

select.form-control {
  appearance: none;
  -webkit-appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 1rem center;
  background-size: 1em;
  padding-right: 2.5rem;
}

.form-control:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  background: white;
}

.wizard-navigation {
  display: flex;
  justify-content: space-between;
  margin-top: 2rem;
  padding-top: 1rem;
  border-top: 1px solid #e2e8f0;
}

.nav-button {
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.nav-button.previous {
  background: #f1f5f9;
  color: #475569;
  border: none;
}

.nav-button.next {
  background: #2563eb;
  color: white;
  border: none;
}

.nav-button.publish {
  background: #10b981;
  color: white;
  border: none;
}

.nav-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.success-message,
.error-message {
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
}

.success-message {
  background: #dcfce7;
  color: #166534;
}

.error-message {
  background: #fee2e2;
  color: #b91c1c;
}

.modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
}

.modal-header {
  padding: 1.5rem;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-body {
  padding: 1.5rem;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #64748b;
}

.lessons-manager {
  margin-top: 1rem;
}

.add-lesson-btn {
  background: #2563eb;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 1rem;
}

.lessons-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.lesson-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.lesson-info {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.lesson-number {
  font-weight: 600;
  color: #64748b;
}

.lesson-title {
  font-weight: 500;
}

.video-status.small {
  font-size: 0.7rem;
  padding: 0.2rem 0.4rem;
  margin-left: 0.5rem;
}

.play-btn {
  background: #dbeafe;
  color: #1d4ed8;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.play-btn:hover {
  background: #bfdbfe;
  transform: translateY(-1px);
}

.play-icon {
  width: 16px;
  height: 16px;
}

.lesson-actions {
  display: flex;
  gap: 0.5rem;
}

.edit-btn,
.delete-btn {
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  border: none;
}

.edit-btn {
  background: #dbeafe;
  color: #1d4ed8;
}

.delete-btn {
  background: #fee2e2;
  color: #b91c1c;
}

.review-section {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.review-item {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.preview-image {
  max-width: 200px;
  border-radius: 8px;
}

.review-lessons {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.review-lesson {
  padding: 0.75rem;
  background: #f8fafc;
  border-radius: 6px;
  font-weight: 500;
  margin-bottom: 0.75rem;
}

.review-lesson-header {
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
}

.lesson-number {
  font-weight: 600;
  margin-right: 0.5rem;
}

.lesson-title {
  flex: 1;
}

.lesson-duration {
  font-size: 0.875rem;
  color: #64748b;
  margin-left: 0.5rem;
}

.lesson-thumbnail {
  margin: 0.5rem 0;
  border-radius: 6px;
  overflow: hidden;
  max-width: 200px;
}

.lesson-thumbnail img {
  width: 100%;
  height: auto;
  display: block;
}

.lesson-video-info {
  display: flex;
  align-items: center;
  margin-top: 0.5rem;
}

.video-status {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.5rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: capitalize;
  margin-right: 0.75rem;
}

.video-status.uploaded {
  background: #dcfce7;
  color: #166534;
}

.video-status.pending {
  background: #fef3c7;
  color: #9a3412;
}

.preview-btn.small {
  padding: 0.25rem 0.75rem;
  font-size: 0.75rem;
  margin-top: 0;
}

.loading-indicator {
  padding: 0.75rem;
  color: #64748b;
  font-style: italic;
  text-align: center;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px dashed #e2e8f0;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 45px;
}

.loading-indicator::before {
  content: "";
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid #e2e8f0;
  border-top-color: #2563eb;
  border-radius: 50%;
  margin-right: 8px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.upload-zone {
  border: 2px dashed #e2e8f0;
  border-radius: 8px;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.upload-zone:hover {
  border-color: #2563eb;
  background: #f8fafc;
}

.upload-placeholder {
  color: #64748b;
  font-weight: 500;
}

.thumbnail-preview {
  margin-top: 1rem;
  border-radius: 8px;
  overflow: hidden;
  max-width: 240px;
  border: 1px solid #e2e8f0;
}

.thumbnail-preview img {
  width: 100%;
  height: auto;
  display: block;
}

.video-preview {
  margin-top: 1rem;
  padding: 1rem;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.video-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.video-icon {
  color: #2563eb;
}

.video-name {
  font-size: 0.875rem;
  font-weight: 500;
  color: #1e293b;
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.preview-btn {
  padding: 0.5rem 1rem;
  background: #2563eb;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;
}

.preview-btn:hover {
  background: #1d4ed8;
  transform: translateY(-1px);
}

@media (max-width: 768px) {
  .wizard-content {
    margin-left: 0;
    padding: 1rem;
  }

  .progress-steps {
    flex-direction: column;
    gap: 1rem;
  }

  .step::before {
    display: none;
  }
}

.file-input-container {
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
}

.file-input {
  width: 0.1px;
  height: 0.1px;
  opacity: 0;
  overflow: hidden;
  position: absolute;
  z-index: -1;
}

.file-input-label {
  background-color: #e2e8f0;
  color: #475569;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-block;
  margin-right: 0.75rem;
}

.file-input-label:hover {
  background-color: #cbd5e1;
}

.file-name {
  color: #64748b;
  font-size: 0.875rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
}

.modal-body form input[type="text"],
.modal-body form input[type="number"],
.modal-body form textarea,
.modal-body form select {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.9375rem;
  transition: all 0.2s ease;
  background: #f8fafc;
}

.modal-body form input:focus,
.modal-body form textarea:focus,
.modal-body form select:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  background: white;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1.5rem;
}

.cancel-btn,
.save-btn {
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.cancel-btn {
  background: #f1f5f9;
  color: #475569;
  border: none;
}

.save-btn {
  background: #2563eb;
  color: white;
  border: none;
}

.save-btn:hover {
  background: #1d4ed8;
  transform: translateY(-1px);
}

.cancel-btn:hover {
  background: #e2e8f0;
}
</style> 